import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type MutableRefObject,
} from "react";

export type TemplateMediaRevealEntry = {
  id: string;
  index: number;
  inView: boolean;
  started: boolean;
  loaded: boolean;
  revealed: boolean;
  requestLoad: () => void;
  reveal: () => void;
};

export type TemplateMediaRevealContextValue = {
  register: (
    entry: Pick<
      TemplateMediaRevealEntry,
      "id" | "index" | "requestLoad" | "reveal"
    > & { inView?: boolean },
  ) => void;
  setInView: (id: string, inView: boolean) => void;
  markLoaded: (id: string) => void;
  unregister: (id: string) => void;
};

export const TemplateMediaRevealContext =
  createContext<TemplateMediaRevealContextValue | null>(null);

export const TemplateMediaIndexContext = createContext<number | null>(null);

/**
 * Search feed: load every tile immediately (no viewport gate). Below-the-fold
 * gating caused cards to blank → paint as you scroll (reads as flicker).
 */
export const TemplateMediaEagerAllContext = createContext(false);

/**
 * Search feed: skip fade/queue reveal and paint `<img>` immediately.
 * Reveal remounts were causing post-paint flicker on results.
 */
export const TemplatePlainMediaContext = createContext(false);

/** First viewport batch — rest wait for IO + lower indices to start. */
const EAGER_INDEX_COUNT = 12;

function resolveTemplateMediaObserverRoot(): Element | null {
  if (typeof document === "undefined") return null;
  const root = document.getElementById("master-template-scroll-root");
  if (!root) return null;
  const style = window.getComputedStyle(root);
  const canScrollY =
    (style.overflowY === "auto" || style.overflowY === "scroll") &&
    root.scrollHeight > root.clientHeight + 8;
  return canScrollY ? root : null;
}

const MAX_MEDIA_IN_FLIGHT = 6;

export function scheduleTemplateMediaRevealTick(
  mapRef: MutableRefObject<Map<string, TemplateMediaRevealEntry>>,
  _revealTimerRef: MutableRefObject<number>,
  _maxWaitTimerRef: MutableRefObject<number>,
  eagerAll = false,
) {
  const entries = [...mapRef.current.values()].sort((a, b) => a.index - b.index);
  const wantsLoad = (e: TemplateMediaRevealEntry) =>
    eagerAll || e.inView || e.index < EAGER_INDEX_COUNT;

  let inFlight = entries.filter((e) => e.started && !e.loaded).length;

  for (const e of entries) {
    if (!wantsLoad(e) || e.started) continue;
    const higherPending = entries.some(
      (p) => p.index < e.index && wantsLoad(p) && !p.started,
    );
    if (higherPending) break;
    if (inFlight >= MAX_MEDIA_IN_FLIGHT) break;
    e.started = true;
    e.requestLoad();
    inFlight++;
  }
}

export function useTemplateMediaRevealContext() {
  return useContext(TemplateMediaRevealContext);
}

export function useTemplateMediaIndex() {
  return useContext(TemplateMediaIndexContext);
}

export type UseTemplateMediaRevealOptions = {
  enabled: boolean;
  src: string | null | undefined;
  /** Explicit grid index; falls back to TemplateMediaIndexProvider. */
  index?: number;
  rootMargin?: string;
  /** First carousel slide / hero — start fetch + reveal as soon as possible. */
  priority?: boolean;
};

export type UseTemplateMediaRevealResult = {
  setRef: (node: HTMLElement | null) => void;
  activeSrc: string | undefined;
  onLoad: () => void;
  onError: () => void;
  wrapperClassName: string;
  revealed: boolean;
};

/**
 * Website-template media reveal:
 * - Queue starts loads top→bottom (index order, max in-flight).
 * - Src attaches only when the queue allows — mid-page cannot race above.
 * - Fade uses opacity only (no translate) to avoid layout flicker.
 */
export function useTemplateMediaReveal({
  enabled,
  src,
  index: indexProp,
  rootMargin = "200px 0px",
  priority = false,
}: UseTemplateMediaRevealOptions): UseTemplateMediaRevealResult {
  const queue = useTemplateMediaRevealContext();
  const eagerAll = useContext(TemplateMediaEagerAllContext);
  const ctxIndex = useTemplateMediaIndex();
  const index = indexProp ?? ctxIndex ?? 0;
  const normalizedSrc = String(src || "").trim();
  const idRef = useRef(
    `mte-media-${Math.random().toString(36).slice(2, 10)}`,
  );
  const [node, setNode] = useState<HTMLElement | null>(null);
  const eager = eagerAll || priority || index < EAGER_INDEX_COUNT;
  const [inView, setInView] = useState(!enabled || eager);
  const [allowed, setAllowed] = useState(!enabled);
  const [activeSrc, setActiveSrc] = useState<string | undefined>(
    !enabled ? normalizedSrc || undefined : undefined,
  );
  const [revealed, setRevealed] = useState(!enabled);

  const requestLoad = useCallback(() => {
    setAllowed(true);
  }, []);

  const reveal = useCallback(() => {
    setRevealed(true);
  }, []);

  useEffect(() => {
    if (!enabled || !queue || !normalizedSrc) return;
    const id = idRef.current;
    queue.register({
      id,
      index,
      inView,
      requestLoad,
      reveal,
    });
    return () => queue.unregister(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, queue, normalizedSrc, index, requestLoad, reveal]);

  useEffect(() => {
    if (!enabled || !queue) return;
    queue.setInView(idRef.current, inView);
  }, [enabled, queue, inView]);

  useEffect(() => {
    if (!enabled) {
      setInView(true);
      return;
    }
    // Eager only marks eligibility — queue still starts loads in index order.
    if (eager) {
      setInView(true);
    }
    if (!node || typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const root = resolveTemplateMediaObserverRoot();
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) setInView(true);
      },
      { root, rootMargin, threshold: 0.01 },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [enabled, rootMargin, node, eager]);

  useEffect(() => {
    if (!enabled || queue) return;
    if (inView) setAllowed(true);
  }, [enabled, queue, inView]);

  useEffect(() => {
    if (!enabled) {
      setActiveSrc(normalizedSrc || undefined);
      setRevealed(true);
      return;
    }
    if (!normalizedSrc) {
      setActiveSrc(undefined);
      setRevealed(true);
      queue?.markLoaded(idRef.current);
      return;
    }
    if (!allowed) {
      setActiveSrc(undefined);
      return;
    }
    setActiveSrc(normalizedSrc);
  }, [enabled, allowed, normalizedSrc, queue]);

  const markLoaded = useCallback(() => {
    if (!queue) {
      reveal();
      return;
    }
    queue.markLoaded(idRef.current);
  }, [queue, reveal]);

  const onLoad = useCallback(() => {
    markLoaded();
  }, [markLoaded]);

  const onError = useCallback(() => {
    markLoaded();
  }, [markLoaded]);

  const setRef = useCallback((el: HTMLElement | null) => {
    setNode(el);
  }, []);

  useEffect(() => {
    if (!enabled || !activeSrc || revealed) return;
    const imgs = node?.querySelectorAll?.(
      ":scope > img, :scope > .mte-tpl-media-reveal-sensor, img.mte-tpl-media-reveal-sensor",
    );
    if (!imgs?.length) return;
    imgs.forEach((img) => {
      if (img instanceof HTMLImageElement && img.complete && img.naturalWidth > 0) {
        markLoaded();
      }
    });
  }, [enabled, activeSrc, revealed, node, markLoaded]);

  const wrapperClassName = !enabled
    ? ""
    : revealed
      ? "mte-tpl-media-ready mte-tpl-media-revealed"
      : activeSrc
        ? "mte-tpl-media-ready mte-tpl-media-pending"
        : "mte-tpl-media-pending";

  return {
    setRef,
    activeSrc,
    onLoad,
    onError,
    wrapperClassName,
    revealed,
  };
}
